import Button from "@/common/components/Button";
import { DateInput } from "@/common/components/DateInput/DateInput";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { FileUploader } from "@/common/components/FileUploader";
import Modal from "@/common/components/Modal";
import ResumeBox from "@/common/components/ResumeBox";
import Select from "@/common/components/Select";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { useEstablishmentRequiredDocuments } from "@/common/hooks/queries/useEstablishmentRequiredDocuments";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { EstablishmentRequiredDocumentData } from "@/common/types/establishmentRequiredDocument";
import { formatDate } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { maintenanceRequiredDocumentSchema, MaintenanceRequiredDocumentValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema";
import { useCreateEstablishmentCompanyMaintenanceRequiredDocuments } from "@/features/establishments/hooks/maintenanceModels/useCreateEstablishmentCompanyMaintenanceRequiredDocuments";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface CreateMaintenanceRequiredDocumentProps {
  establishmentCompany?: EstablishmentCompanyData;
  disabled?: boolean;
}

const CreateMaintenanceRequiredDocument = ({ establishmentCompany, disabled }: CreateMaintenanceRequiredDocumentProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [searchRequiredDocuments, setSearchRequiredDocuments] = useDebounce('');

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm<MaintenanceRequiredDocumentValidationSchema>({
    resolver: zodResolver(maintenanceRequiredDocumentSchema),
    defaultValues: {
      document: undefined,
      file_attachment_attributes: undefined,
      valid_until: undefined,
      issue_date: undefined,
    }
  });

  const {
    mutateAsync: uploadBlob,
    isPending: uploadBlobIsPending,
  } = useBlobs();

  const {
    mutate: createMaintenanceRequiredDocument,
    isPending: createMaintenanceRequiredDocumentIsPending,
  } = useCreateEstablishmentCompanyMaintenanceRequiredDocuments({
    establishmentCompanyId: establishmentCompany?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishmentCompany?.id] });
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({
        setError,
      })(error);
    },
  });

  const onSubmit: SubmitHandler<MaintenanceRequiredDocumentValidationSchema> = (data) => createMaintenanceRequiredDocument(data);

  const {
    data: requiredDocumentsOptions,
  } = useEstablishmentRequiredDocuments({
    filters: {
      search: searchRequiredDocuments,
    },
    mapper: Mapper.mapToOptionsWithItem<EstablishmentRequiredDocumentData>({
      valueFieldName: 'id',
      labelFieldName: 'display_name',
    }),
  });

  const resetForm = () => {
    reset({
      document: undefined,
      file_attachment_attributes: undefined,
      valid_until: undefined,
      issue_date: undefined,
    });
  }

  const handleAppendRequiredDocument = (requiredDocument: EstablishmentRequiredDocumentData) => {
    reset({
      document: {
        id: requiredDocument.id,
        display_name: requiredDocument.display_name,
      },
      file_attachment_attributes: {
        blob_id: '',
        file_name: '',
        file_url: '',
      },
      issue_date: '',
      valid_until: requiredDocument.has_default_validity_in_days
        ? requiredDocument.default_validity_in_days
          ? formatDate(addDays(new Date(), requiredDocument.default_validity_in_days))
          : ''
        : '',
    });
  }

  const handleUploadDocument = async (file: File) => {
    const blob = await uploadBlob({ file, filename: file.name });

    const blobId = blob?.data?.data?.id;

    setValue(`file_attachment_attributes.blob_id`, blobId);

    setValue(`file_attachment_attributes.file_url`, URL.createObjectURL(file));

    setValue(`file_attachment_attributes.file_name`, file.name);
  }

  useEffect(() => {
    if (!isOpen) {
      setTimeout(resetForm, 500);
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerDisabled={disabled}
        Trigger={(
          <Button
            operationKlassToCheckPermission="Admin::Establishment::Companies::UpdateMaintenanceModels"
            disabled={disabled}
          >
            Adicionar
          </Button>
        )}
        title="Adicionar Documento do estabelecimento"
        useCancellableFooter={!!watch('document')}
        onCancel={(event) => {
          event.preventDefault();

          resetForm();
        }}
        footer="Cancelar"
        cancelButtonText="Voltar"
        footerClassName="bg-white-200 text-white-800 rounded-b-lg"
        cancelButtonClassName="bg-white-200 rounded-es-lg"
        confirmButtonClassName="bg-blue-700 rounded-ee-lg"
        confirmButtonIsLoading={createMaintenanceRequiredDocumentIsPending}
        onConfirm={() => {
          const hasDocument = !!watch('document');

          if (hasDocument) {
            handleSubmit(onSubmit)();
          } else {
            setIsOpen(false);
          }
        }}
        body={(
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 text-white-800">
            {!watch('document') && (
              <div className="min-h-72">
                <Select
                  placeholder={(
                    <div className="flex items-center gap-x-2 text-white-800">
                      <SearchIcon strokeWidth={1.5} className="size-5" />

                      <p className="text-sm text-white-300">
                        Pesquisar documentos que possui
                      </p>
                    </div>
                  )}
                  onInputChange={setSearchRequiredDocuments}
                  options={requiredDocumentsOptions.filter((requiredDocument) => {
                    return !establishmentCompany?.maintenance_required_documents?.some((maintenanceRequiredDocument) => {
                      return maintenanceRequiredDocument.document.id === requiredDocument.value
                    });
                  })}
                  value={null}
                  onChange={(newValue) => handleAppendRequiredDocument((newValue as { item: EstablishmentRequiredDocumentData }).item)}
                  isDisabled={createMaintenanceRequiredDocumentIsPending}
                />
              </div>
            )}

            {watch('document') && (
              <div className="flex flex-col gap-3 bg-white-100 p-3 rounded-lg lg:bg-transparent lg:p-0 lg:rounded-none">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-4">
                    <ResumeBox
                      label="Nome do documento"
                      value={watch('document').display_name}
                      padding="small"
                      boxClassName="bg-white-200"
                      labelClassName="text-sm text-[11px]"
                      valueClassName="text-sm font-bold"
                    />

                    <div className="flex flex-col xl:flex-row gap-3">
                      <DateInput
                        label="Data de emissão"
                        control={control}
                        fieldName={`issue_date`}
                        errorMessage={errors?.issue_date?.message || ''}
                        disabled={createMaintenanceRequiredDocumentIsPending}
                        value={watch('issue_date')}
                        className="flex-1"
                      />

                      <DateInput
                        label="Validade do documento"
                        control={control}
                        fieldName={`valid_until`}
                        errorMessage={errors?.valid_until?.message || ''}
                        disabled={createMaintenanceRequiredDocumentIsPending}
                        value={watch('valid_until')}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1">
                    <FileUploader
                      title="Anexe documentos"
                      className="min-h-[108px] w-full bg-yellow-op-2 border-none"
                      accept=".png, .jpg, .webp, .pdf"
                      handleChange={handleUploadDocument}
                      disabled={createMaintenanceRequiredDocumentIsPending || uploadBlobIsPending}
                      isLoading={uploadBlobIsPending}
                      maxFileSizeInMb={20}
                      currentFileUrl={watch('file_attachment_attributes')?.file_url}
                      fileName={watch('file_attachment_attributes')?.file_name}
                    />

                    {errors?.file_attachment_attributes?.file_name?.message && (
                      <ErrorMessage
                        name={`file_attachment_attributes.file_name`}
                        message={errors?.file_attachment_attributes?.file_name.message}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </form >
        )}
      />
    </>
  );
}

export default CreateMaintenanceRequiredDocument;
